import PropTypes from 'prop-types';
import {
  useColorModeValue,
  Text,
  Box,
  IconButton,
  Flex,
  Tooltip,
  Spacer,
} from '@chakra-ui/react';

import { ChevronRightIcon, CloseIcon, DragHandleIcon } from '@chakra-ui/icons';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { learningPlanCourseActions } from 'store/modules/learningPlanCourse/learningPlanCourse-slice';
import EmailGroup from 'components/common/fields/EmailGroup';
import PublishTag from 'components/common/fields/PublishTag';
import AccessTag from 'components/common/fields/AccessTag';
import TagGroup from 'components/common/fields/TagGroup';
import IdTag from 'components/common/fields/IdTag';
import { courseActions } from 'store/modules/course/course-slice';

function LearningPlanCourse({ id, course, isPreviewMode }) {
  const dispatch = useDispatch();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const learningPlan = useSelector((state) => state.learningPlan.learningPlan);

  const style = { transition, transform: CSS.Transform.toString(transform) };

  const handleDeleteButton = (courseId) => {
    dispatch(
      learningPlanCourseActions.setDeleteLearningPlanCourse({
        modal: true,
        learningPlanId: learningPlan.id,
        courseId,
      })
    );
  };

  const handlePreviewButton = (courseId) => {
    dispatch(
      courseActions.setCoursePreview({
        modal: true,
        id: courseId,
      })
    );
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box mb={4} borderWidth="1px" borderRadius="md">
        <Box w="100%" p={4} bg={useColorModeValue('#f7f7f7', 'gray.800')}>
          <Flex wrap="wrap" align="center">
            {!isPreviewMode && (
              <Tooltip label="Drag to rearrange the order">
                <IconButton
                  {...attributes}
                  {...listeners}
                  variant="plain"
                  color="teal"
                  aria-label="Re-order course"
                  icon={<DragHandleIcon />}
                />
              </Tooltip>
            )}
            <IdTag id={course.id} />

            <Text fontWeight={500} mx={2} fontSize="md" flex={1}>
              {course.name}
            </Text>

            {!isPreviewMode && (
              <Tooltip label="Remove" fontSize="md">
                <IconButton
                  onClick={() => handleDeleteButton(course.id)}
                  size="xs"
                  variant="ghost"
                  icon={<CloseIcon />}
                />
              </Tooltip>
            )}
          </Flex>

          <TagGroup tags={course?.tags} />

          <Flex wrap="wrap" direction="column" mt={3} gap={3} align="start">
            <Text fontSize="sm">{course.description}</Text>
            <Text fontSize="xs" fontWeight={500}>
              Owner: {course.owner?.name || 'N/A'}
            </Text>

            <EmailGroup name="Contributors:" emails={course.ownerGroups} />

            <Flex align="center" width="100%" gap={2}>
              <PublishTag isPublished={course.isPublished} />
              <AccessTag isPrivate={course.isPrivate} />
              <Spacer />
              <Tooltip label="Preview" fontSize="md">
                <IconButton
                  onClick={() => handlePreviewButton(course.id)}
                  colorScheme="blue"
                  variant="ghost"
                  aria-label="Preview course"
                  fontSize="40px"
                  icon={<ChevronRightIcon />}
                />
              </Tooltip>
            </Flex>

            <EmailGroup name="Allowed Access:" emails={course.targetGroups} />
          </Flex>
        </Box>
      </Box>
    </div>
  );
}

LearningPlanCourse.defaultProps = {
  isPreviewMode: false,
};

LearningPlanCourse.propTypes = {
  id: PropTypes.number.isRequired,
  course: PropTypes.object.isRequired,
  isPreviewMode: PropTypes.bool,
};

export default LearningPlanCourse;
